import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AdvertisingActivityPanel = ({ lead, onAnalyze }) => {
  const [activeChannel, setActiveChannel] = useState('all');

  const advertisingChannels = [
    {
      id: 'google-ads',
      name: 'Google Ads',
      icon: 'Search',
      active: lead?.advertising?.googleAds?.active || false,
      budget: lead?.advertising?.googleAds?.estimatedBudget || 0,
      campaigns: lead?.advertising?.googleAds?.activeCampaigns || 0,
      keywords: lead?.advertising?.googleAds?.targetedKeywords || [],
      performance: lead?.advertising?.googleAds?.performance || 0
    },
    {
      id: 'facebook-ads',
      name: 'Facebook Ads',
      icon: 'Facebook',
      active: lead?.advertising?.facebookAds?.active || false,
      budget: lead?.advertising?.facebookAds?.estimatedBudget || 0,
      campaigns: lead?.advertising?.facebookAds?.activeCampaigns || 0,
      targeting: lead?.advertising?.facebookAds?.audienceSize || 0,
      performance: lead?.advertising?.facebookAds?.performance || 0
    },
    {
      id: 'linkedin-ads',
      name: 'LinkedIn Ads',
      icon: 'Linkedin',
      active: lead?.advertising?.linkedinAds?.active || false,
      budget: lead?.advertising?.linkedinAds?.estimatedBudget || 0,
      campaigns: lead?.advertising?.linkedinAds?.activeCampaigns || 0,
      targeting: lead?.advertising?.linkedinAds?.audienceSize || 0,
      performance: lead?.advertising?.linkedinAds?.performance || 0
    },
    {
      id: 'instagram-ads',
      name: 'Instagram Ads',
      icon: 'Instagram',
      active: lead?.advertising?.instagramAds?.active || false,
      budget: lead?.advertising?.instagramAds?.estimatedBudget || 0,
      campaigns: lead?.advertising?.instagramAds?.activeCampaigns || 0,
      targeting: lead?.advertising?.instagramAds?.audienceSize || 0,
      performance: lead?.advertising?.instagramAds?.performance || 0
    }
  ];

  const formatBudget = (budget) => {
    if (!budget) return '$0';
    if (budget >= 1000000) return `$${(budget / 1000000)?.toFixed(1)}M`;
    if (budget >= 1000) return `$${(budget / 1000)?.toFixed(1)}K`;
    return `$${budget}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000)?.toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000)?.toFixed(1)}K`;
    return num?.toString();
  };

  const getPerformanceColor = (score) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceLabel = (score) => {
    if (score >= 75) return 'High';
    if (score >= 50) return 'Medium';
    return 'Low';
  };

  const activeChannels = advertisingChannels?.filter(ch => ch?.active);
  const totalBudget = activeChannels?.reduce((sum, ch) => sum + ch?.budget, 0);
  const totalCampaigns = activeChannels?.reduce((sum, ch) => sum + ch?.campaigns, 0);

  const displayChannels = activeChannel === 'all' 
    ? advertisingChannels 
    : advertisingChannels?.filter(ch => ch?.id === activeChannel);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-green-100 p-2 rounded-lg">
            <Icon name="TrendingUp" size={24} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Advertising Activity</h3>
            <p className="text-sm text-muted-foreground">Competitive advertising intelligence</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          onClick={() => onAnalyze?.(lead)}
        >
          Refresh Analysis
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={20} className="text-green-600" />
            <span className="text-xs px-2 py-1 rounded-full bg-green-500 text-white">
              Est. Total
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Monthly Ad Spend</p>
          <p className="text-2xl font-bold text-foreground">{formatBudget(totalBudget)}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Target" size={20} className="text-blue-600" />
            <span className="text-xs px-2 py-1 rounded-full bg-blue-500 text-white">
              Active
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Total Campaigns</p>
          <p className="text-2xl font-bold text-foreground">{totalCampaigns}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Zap" size={20} className="text-purple-600" />
            <span className="text-xs px-2 py-1 rounded-full bg-purple-500 text-white">
              {activeChannels?.length} Channels
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Ad Platforms</p>
          <p className="text-2xl font-bold text-foreground">{activeChannels?.length}/4</p>
        </div>
      </div>

      {/* Channel Filter */}
      <div className="flex items-center space-x-2 mb-4 overflow-x-auto pb-2">
        <Button
          variant={activeChannel === 'all' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setActiveChannel('all')}
        >
          All Channels
        </Button>
        {advertisingChannels?.map(channel => (
          <Button
            key={channel?.id}
            variant={activeChannel === channel?.id ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setActiveChannel(channel?.id)}
            iconName={channel?.icon}
            disabled={!channel?.active}
          >
            {channel?.name}
          </Button>
        ))}
      </div>

      {/* Channel Details */}
      <div className="space-y-4">
        {displayChannels?.map(channel => (
          <div
            key={channel?.id}
            className={`border rounded-lg p-4 ${
              channel?.active 
                ? 'border-primary/20 bg-primary/5' :'border-border bg-muted/50 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Icon name={channel?.icon} size={24} className="text-primary" />
                <div>
                  <h4 className="font-semibold text-foreground">{channel?.name}</h4>
                  {channel?.active ? (
                    <span className="text-xs text-success flex items-center mt-1">
                      <span className="w-2 h-2 bg-success rounded-full mr-1 animate-pulse"></span>
                      Active Campaign Running
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground mt-1">No Active Campaigns</span>
                  )}
                </div>
              </div>
              {channel?.active && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  channel?.performance >= 75 ? 'bg-green-100 text-green-700' :
                  channel?.performance >= 50 ? 'bg-yellow-100 text-yellow-700': 'bg-red-100 text-red-700'
                }`}>
                  {getPerformanceLabel(channel?.performance)} Performance
                </span>
              )}
            </div>

            {channel?.active && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-card p-3 rounded border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Est. Budget</p>
                  <p className="text-sm font-semibold text-foreground">{formatBudget(channel?.budget)}/mo</p>
                </div>
                <div className="bg-card p-3 rounded border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Campaigns</p>
                  <p className="text-sm font-semibold text-foreground">{channel?.campaigns}</p>
                </div>
                {channel?.keywords && (
                  <div className="bg-card p-3 rounded border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Keywords</p>
                    <p className="text-sm font-semibold text-foreground">{channel?.keywords?.length}</p>
                  </div>
                )}
                {channel?.targeting && (
                  <div className="bg-card p-3 rounded border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Audience</p>
                    <p className="text-sm font-semibold text-foreground">{formatNumber(channel?.targeting)}</p>
                  </div>
                )}
                {channel?.performance && (
                  <div className="bg-card p-3 rounded border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Performance</p>
                    <p className={`text-sm font-semibold ${getPerformanceColor(channel?.performance)}`}>
                      {channel?.performance}%
                    </p>
                  </div>
                )}
              </div>
            )}

            {channel?.active && channel?.keywords && channel?.keywords?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground mb-2">Top Keywords</p>
                <div className="flex flex-wrap gap-2">
                  {channel?.keywords?.slice(0, 6)?.map((keyword, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                  {channel?.keywords?.length > 6 && (
                    <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full">
                      +{channel?.keywords?.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Competitive Insights */}
      <div className="border-t border-border pt-4 mt-6">
        <h4 className="font-semibold text-foreground mb-3 flex items-center">
          <Icon name="Lightbulb" size={16} className="mr-2 text-warning" />
          Competitive Insights
        </h4>
        <div className="space-y-2">
          {lead?.advertising?.insights?.map((insight, index) => (
            <div key={index} className="flex items-start space-x-2 p-2 bg-warning/10 border border-warning/20 rounded">
              <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
              <p className="text-sm text-foreground">{insight}</p>
            </div>
          )) || (
            <p className="text-sm text-muted-foreground italic">
              No competitive insights available yet. Run analysis to generate recommendations.
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center space-x-3 mt-6 pt-4 border-t border-border">
        <Button variant="primary" size="sm" iconName="Target" className="flex-1">
          View Ad Strategy
        </Button>
        <Button variant="outline" size="sm" iconName="Download" className="flex-1">
          Export Report
        </Button>
      </div>
    </div>
  );
};

export default AdvertisingActivityPanel;